if (!customElements.get('td-bundle-quantity-selector')) {
  class TdBundleQuantitySelector extends HTMLElement {
    constructor() {
      super();
      
      // Store current purchase type state
      this.purchaseType = {
        isOneTime: true,
        hasSubscription: false
      };
      
      // Store observer reference for cleanup
      this.mutationObserver = null;
      
      // Store button references
      this.quantityButtons = [];
      this.bundleButton = null;
      
      // Store currently selected button
      this.selectedButton = null;
      
      // Store form reference
      this.productForm = null;
    }

    connectedCallback() {
      // Detect initial state on page load
      this.detectPurchaseType();
      
      // Set up listeners for future changes
      this.setupPurchaseTypeListener();
      
      // Set up button listeners
      this.setupButtonListeners();
      
      // Find and set initially active button
      this.setInitialActiveButton();
      
      // Set up form submission listener
      this.setupFormListener();
      
      // Set initial inactive state based on purchase type
      this.updateButtonInactiveState();
      
      // Set initial price container visibility
      this.updatePriceContainerVisibility();
      
      // Set initial discount value visibility
      this.updateDiscountValueVisibility();
      
      // Set initial ATC price
      this.updateATCPrice();
      
      // Set initial purchase type button prices
      this.updatePurchaseTypeButtonPrices();

      // Set initial shipping callout state
      this.updateShippingCallout();
    }

    disconnectedCallback() {
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }
      if (this.skioObserver) {
        this.skioObserver.disconnect();
        this.skioObserver = null;
      }
    }

    /**
     * Detects whether user has selected one-time purchase or subscription
     * Checks multiple sources to determine the current state
     * @returns {Object} Object with isOneTime and hasSubscription boolean properties
     */
    detectPurchaseType() {
      const sellingPlanInput = document.querySelector('input[name="selling_plan"]');

      // The selling plan input's .value property is the source of truth.
      // Skio may update it via property (not attribute), so always read the property.
      const sellingPlanValue = sellingPlanInput ? sellingPlanInput.value : '';
      const hasActiveSubscription = !!sellingPlanValue;
      const isOneTimePurchase = !hasActiveSubscription;

      this.purchaseType = {
        isOneTime: isOneTimePurchase,
        hasSubscription: hasActiveSubscription
      };
      
      return this.purchaseType;
    }

    /**
     * Sets up a single listener to detect when purchase type changes
     * Watches the selling plan input which is the source of truth
     */
    setupPurchaseTypeListener() {
      const sellingPlanInput = document.querySelector('input[name="selling_plan"]');
      if (!sellingPlanInput) return;
      
      // MutationObserver for attribute-based value changes
      this.mutationObserver = new MutationObserver(() => {
        this.handlePurchaseTypeChange();
      });
      this.mutationObserver.observe(sellingPlanInput, {
        attributes: true,
        attributeFilter: ['value']
      });

      // Listen for Skio's custom event (fires on property-based changes)
      const skioPlanPicker = document.querySelector('skio-plan-picker');
      if (skioPlanPicker) {
        skioPlanPicker.addEventListener('skio::update-selling-plan', () => {
          this.handlePurchaseTypeChange();
        });
      } else {
        // Skio may load later — watch for it
        this.skioObserver = new MutationObserver(() => {
          const picker = document.querySelector('skio-plan-picker');
          if (picker) {
            picker.addEventListener('skio::update-selling-plan', () => {
              this.handlePurchaseTypeChange();
            });
            this.skioObserver.disconnect();
            this.skioObserver = null;
          }
        });
        this.skioObserver.observe(document.body, { childList: true, subtree: true });
      }
    }

    /**
     * Handles purchase type changes
     * Re-detects the state and updates internal state
     */
    handlePurchaseTypeChange() {
      this.detectPurchaseType();
      
      // Update button inactive state based on purchase type
      this.updateButtonInactiveState();
      
      // Update price container visibility
      this.updatePriceContainerVisibility();
      
      // Update discount value visibility
      this.updateDiscountValueVisibility();
      
      // Update ATC price
      this.updateATCPrice();
      
      // Update purchase type button prices
      this.updatePurchaseTypeButtonPrices();

      // Update shipping callout based on new purchase type
      this.updateShippingCallout();
    }

    /**
     * Sets up event listeners for quantity and bundle buttons
     */
    setupButtonListeners() {
      // Find all quantity buttons
      this.quantityButtons = Array.from(this.querySelectorAll('[data-quantity]'));
      
      // Find bundle button
      this.bundleButton = this.querySelector('[data-bundle-product-id]');
      
      // Check and log selling plans attribute for all buttons
      this.checkSellingPlansAttribute();
      
      // Add click listeners to quantity buttons
      this.quantityButtons.forEach(button => {
        button.addEventListener('click', () => {
          this.handleButtonClick(button, 'quantity');
        });
      });
      
      // Add click listener to bundle button if it exists
      if (this.bundleButton) {
        this.bundleButton.addEventListener('click', () => {
          this.handleButtonClick(this.bundleButton, 'bundle');
        });
      }
      
      // Add click listeners to Skio purchase type buttons
      this.setupPurchaseTypeButtonListeners();
    }

    /**
     * Checks for data-has-selling-plans attribute on all buttons
     */
    checkSellingPlansAttribute() {
      // This method can be used for future validation if needed
      // Currently just checking that buttons exist with the attribute
    }

    /**
     * Finds and sets the initially active button on page load
     */
    setInitialActiveButton() {
      // Find button with active class
      const activeButton = this.querySelector('.td-quantity-button--active');
      if (activeButton) {
        this.selectedButton = activeButton;
        
        // Update bundle additional info container class
        this.updateBundleAdditionalInfoContainer();
        
        // Update quantity input if it's a quantity button
        if (activeButton.hasAttribute('data-quantity')) {
          this.updateQuantityInput(activeButton);
        }
      }
    }

    /**
     * Sets up event listeners for Skio purchase type buttons
     */
    setupPurchaseTypeButtonListeners() {
      // Listen for clicks on Skio group containers (one-time and subscription buttons)
      const groupContainers = document.querySelectorAll('.group-container');
      groupContainers.forEach(container => {
        container.addEventListener('click', () => {
          // Use setTimeout to allow DOM to update after click
          setTimeout(() => {
            this.handlePurchaseTypeButtonClick();
          }, 0);
        });
      });
      
      // Also listen to radio inputs directly
      const oneTimeInputs = document.querySelectorAll('.td-one-time-group-input');
      const subscriptionInputs = document.querySelectorAll('.td-subscription-group-input');
      
      oneTimeInputs.forEach(input => {
        input.addEventListener('click', () => {
          this.handlePurchaseTypeButtonClick();
        });
      });
      
      subscriptionInputs.forEach(input => {
        input.addEventListener('click', () => {
          this.handlePurchaseTypeButtonClick();
        });
      });
    }

    /**
     * Handles purchase type button clicks
     */
    handlePurchaseTypeButtonClick() {
      // Re-detect purchase type after click
      this.detectPurchaseType();
      
      // Update button inactive state based on purchase type
      this.updateButtonInactiveState();
      
      // Update price container visibility
      this.updatePriceContainerVisibility();
      
      // Update discount value visibility
      this.updateDiscountValueVisibility();
      
      // Update ATC price
      this.updateATCPrice();

      // Update shipping callout for the active purchase type
      this.updateShippingCallout();
    }

    /**
     * Handles button click events
     */
    handleButtonClick(button, buttonType) {
      // Remove active class from all buttons
      this.quantityButtons.forEach(btn => {
        btn.classList.remove('td-quantity-button--active');
      });
      if (this.bundleButton) {
        this.bundleButton.classList.remove('td-quantity-button--active');
      }
      
      // Add active class to clicked button
      button.classList.add('td-quantity-button--active');
      
      // Update selected button reference
      this.selectedButton = button;
      
      // Update bundle additional info container class
      this.updateBundleAdditionalInfoContainer();
      
      // Update Subscribe button inactive state based on clicked button's selling plans
      this.updateSubscribeButtonInactiveState(button);

      // Re-detect purchase type to catch any state Skio set via property
      this.detectPurchaseType();
      
      // Update ATC price
      this.updateATCPrice();
      
      // Update quantity input if it's a quantity button (not bundle)
      if (buttonType === 'quantity') {
        this.updateQuantityInput(button);
      }
      
      // Update purchase type button prices
      this.updatePurchaseTypeButtonPrices();

      // Update shipping callout based on selected button
      this.updateShippingCallout();
    }

    /**
     * Sets up form submission listener for bundle products
     */
    setupFormListener() {
      // Find the product form
      this.productForm = this.closest('form') || document.querySelector('form[data-type="add-to-cart-form"]');
      
      if (!this.productForm) return;
      
      // Listen for form submission with capture to catch it early
      this.productForm.addEventListener('submit', (event) => {
        if (this.isBundleSelected()) {
          this.handleBundleProductSubmission(event);
        }
      }, true);
      
      // Also listen on submit button directly with capture
      const submitButton = this.productForm.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.addEventListener('click', (event) => {
          if (this.isBundleSelected()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.handleBundleProductSubmission(event);
            return false;
          }
        }, true);
      }
    }

    /**
     * Checks if bundle product is currently selected
     */
    isBundleSelected() {
      return this.selectedButton && this.selectedButton.hasAttribute('data-bundle-product-id');
    }

    /**
     * Updates the bundle additional info container class based on bundle selection
     */
    updateBundleAdditionalInfoContainer() {
      const container = document.querySelector('[data-bundle-additional-info-container]');
      if (container) {
        if (this.isBundleSelected()) {
          container.classList.add('td-quantity-buttons__additional-info-container--active');
        } else {
          container.classList.remove('td-quantity-buttons__additional-info-container--active');
        }
      }
    }

    /**
     * Handles bundle product form submission
     */
    handleBundleProductSubmission(event) {
      // Prevent default form submission
      event.preventDefault();
      event.stopPropagation();
      
      // Get bundle product info from selected button
      const bundleVariantId = this.selectedButton.getAttribute('data-bundle-product-variant-id');
      
      if (!bundleVariantId) {
        console.error('[Bundle Quantity Selector] Bundle variant ID not found');
        return;
      }
      
      // Prepare cart data
      const cartData = {
        items: [{
          id: bundleVariantId,
          quantity: 1
        }]
      };
      
      // Add bundle product to cart
      this.addBundleProductToCart(cartData);
    }

    /**
     * Adds bundle product to cart using Cart API
     */
    async addBundleProductToCart(cartData) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(cartData)
        });

        if (response.ok) {
          // Consume response body
          await response.json();
          
          // Handle cart update - dispatch event for theme to handle
          const addToCartEvent = new CustomEvent('add-to-cart', {
            bubbles: true,
            detail: { bundleProduct: true }
          });
          
          // Dispatch the event on the product form so the theme can catch it
          const productForm = document.querySelector('.product__form, form[data-type="add-to-cart-form"]');
          if (productForm) {
            productForm.dispatchEvent(addToCartEvent);
            return;
          }
          
          // Fallback: Direct cart drawer opening
          const cartDrawer = document.getElementById('site-cart-sidebar');
          if (cartDrawer && typeof cartDrawer.show === 'function') {
            cartDrawer.show();
            return;
          }
        } else {
          const errorData = await response.json();
          console.error('[Bundle Quantity Selector] Cart error:', errorData);
        }
      } catch (error) {
        console.error('[Bundle Quantity Selector] Cart error:', error);
      }
    }

    /**
     * Updates inactive state of Subscribe button based on clicked button's selling plans
     */
    updateSubscribeButtonInactiveState(button) {
      // Find the skio-plan-picker element and access shadow root
      const skioPlanPicker = document.querySelector('skio-plan-picker');
      if (!skioPlanPicker || !skioPlanPicker.shadowRoot) return;
      
      // Find the Subscribe button within shadow root
      const subscribeButton = skioPlanPicker.shadowRoot.querySelector('.skio-subscription-button.group-container');
      if (!subscribeButton) return;
      
      // Check if clicked button has selling plans
      const hasSellingPlans = button.getAttribute('data-has-selling-plans') === 'true';
      
      // Set display: none if button has no selling plans, otherwise show it
      subscribeButton.style.display = hasSellingPlans ? '' : 'none';
    }

    /**
     * Updates prices in Purchase Type buttons (one-time and subscription) based on selected button
     */
    updatePurchaseTypeButtonPrices() {
      if (!this.selectedButton) return;
      
      // Find the skio-plan-picker element and access shadow root
      const skioPlanPicker = document.querySelector('skio-plan-picker');
      if (!skioPlanPicker || !skioPlanPicker.shadowRoot) return;
      
      const shadowRoot = skioPlanPicker.shadowRoot;
      
      // Get prices from selected button
      const oneTimePrice = this.selectedButton.getAttribute('data-one-time-price') || '';
      const oneTimeComparePrice = this.selectedButton.getAttribute('data-one-time-compare-price') || '';
      const subscriptionPrice = this.selectedButton.getAttribute('data-subscription-price') || '';
      const subscriptionComparePrice = this.selectedButton.getAttribute('data-subscription-compare-price') || '';
      
      // Find and update one-time button price
      const oneTimeInput = shadowRoot.querySelector('.td-one-time-group-input');
      if (oneTimeInput) {
        const oneTimeContainer = oneTimeInput.closest('.group-container');
        if (oneTimeContainer) {
          const oneTimePriceElement = oneTimeContainer.querySelector('.skio-price');
          if (oneTimePriceElement) {
            oneTimePriceElement.textContent = oneTimePrice;
          }
          
          // Update compare price (always set, even if empty, to clear previous value)
          const oneTimeComparePriceElement = oneTimeContainer.querySelector('.td-skio-price-wrapper-original');
          if (oneTimeComparePriceElement) {
            oneTimeComparePriceElement.textContent = oneTimeComparePrice;
          }
        }
      }
      
      // Find and update subscription button price
      const subscribeButton = shadowRoot.querySelector('.skio-subscription-button.group-container');
      if (subscribeButton) {
        const subscriptionPriceElement = subscribeButton.querySelector('.skio-price');
        if (subscriptionPriceElement) {
          subscriptionPriceElement.textContent = subscriptionPrice;
        }
        
        // Update compare price (always set, even if empty, to clear previous value)
        const subscriptionComparePriceElement = subscribeButton.querySelector('.td-skio-price-wrapper-original');
        if (subscriptionComparePriceElement) {
          subscriptionComparePriceElement.textContent = subscriptionComparePrice;
        }
      }
    }

    /**
     * Gets information about the currently selected button
     */
    getSelectedButtonInfo() {
      if (!this.selectedButton) {
        return null;
      }
      
      const info = {
        element: this.selectedButton,
        isQuantity: this.selectedButton.hasAttribute('data-quantity'),
        isBundle: this.selectedButton.hasAttribute('data-bundle-product-id'),
        hasSellingPlans: this.selectedButton.hasAttribute('data-has-selling-plans'),
        sellingPlansValue: this.selectedButton.getAttribute('data-has-selling-plans')
      };
      
      if (info.isQuantity) {
        info.quantity = this.selectedButton.getAttribute('data-quantity');
      }
      
      if (info.isBundle) {
        info.bundleProductId = this.selectedButton.getAttribute('data-bundle-product-id');
        info.bundleVariantId = this.selectedButton.getAttribute('data-bundle-product-variant-id');
      }
      
      return info;
    }


    /**
     * Updates inactive state of buttons based on purchase type
     * When subscription is selected, hides buttons without selling plans
     * When one-time is selected, shows all buttons
     */
    updateButtonInactiveState() {
      // If subscription is selected
      if (this.purchaseType.hasSubscription) {
        // Hide quantity buttons without selling plans
        this.quantityButtons.forEach(button => {
          const hasSellingPlans = button.getAttribute('data-has-selling-plans') === 'true';
          button.style.display = hasSellingPlans ? '' : 'none';
        });
        
        // Hide bundle button if it doesn't have selling plans
        if (this.bundleButton) {
          const hasSellingPlans = this.bundleButton.getAttribute('data-has-selling-plans') === 'true';
          this.bundleButton.style.display = hasSellingPlans ? '' : 'none';
        }
      } else {
        // One-time purchase selected - show all buttons
        this.quantityButtons.forEach(button => {
          button.style.display = '';
        });
        
        if (this.bundleButton) {
          this.bundleButton.style.display = '';
        }
      }
    }

    /**
     * Updates price container visibility based on purchase type
     * Shows one-time price container when one-time is selected
     * Shows subscription price container when subscription is selected
     */
    updatePriceContainerVisibility() {
      const allButtons = [...this.quantityButtons];
      if (this.bundleButton) {
        allButtons.push(this.bundleButton);
      }
      
      allButtons.forEach(button => {
        const oneTimeContainer = button.querySelector('.td-quantity-buttons__one-time-price-container');
        const subscriptionContainer = button.querySelector('.td-quantity-buttons__subscription-price-container');
        
        if (this.purchaseType.isOneTime) {
          // Show one-time container, hide subscription container
          if (oneTimeContainer) {
            oneTimeContainer.style.display = 'flex';
          }
          if (subscriptionContainer) {
            subscriptionContainer.style.display = 'none';
          }
        } else if (this.purchaseType.hasSubscription) {
          // Show subscription container, hide one-time container
          if (oneTimeContainer) {
            oneTimeContainer.style.display = 'none';
          }
          if (subscriptionContainer) {
            subscriptionContainer.style.display = 'flex';
          }
        }
      });
    }

    /**
     * Updates visibility of discount value divs based on purchase type
     */
    updateDiscountValueVisibility() {
      const allButtons = [...this.quantityButtons];
      if (this.bundleButton) {
        allButtons.push(this.bundleButton);
      }
      
      allButtons.forEach(button => {
        const oneTimeDiscount = button.querySelector('.td-quantity__one-time-discount-value');
        const subscriptionDiscount = button.querySelector('.td-quantity__subscription-discount-value');
        
        if (this.purchaseType.isOneTime) {
          // Show one-time discount, hide subscription discount
          if (oneTimeDiscount) {
            oneTimeDiscount.style.display = 'block';
          }
          if (subscriptionDiscount) {
            subscriptionDiscount.style.display = 'none';
          }
        } else if (this.purchaseType.hasSubscription) {
          // Show subscription discount, hide one-time discount
          if (oneTimeDiscount) {
            oneTimeDiscount.style.display = 'none';
          }
          if (subscriptionDiscount) {
            subscriptionDiscount.style.display = 'block';
          }
        }
      });
    }

    /**
     * Updates the quantity input field based on selected quantity button
     */
    updateQuantityInput(button) {
      // Find the quantity input field
      const quantityInput = this.querySelector('.td-quantity-input');
      if (!quantityInput) return;
      
      // Get quantity from button's data attribute
      const quantity = button.getAttribute('data-quantity');
      if (quantity) {
        quantityInput.value = quantity;
      }
    }

    /**
     * Updates the add to cart button price based on selected button and purchase type
     */
    updateATCPrice() {
      // Find ATC price elements
      const atcPriceElement = document.querySelector('[data-quantity-price]');
      const atcComparePriceElement = document.querySelector('[data-quantity-compare-price]');
      
      if (!atcPriceElement || !this.selectedButton) return;
      
      // Get price values from selected button based on purchase type
      let price = '';
      let comparePrice = '';
      
      if (this.purchaseType.isOneTime) {
        price = this.selectedButton.getAttribute('data-one-time-price') || '';
        comparePrice = this.selectedButton.getAttribute('data-one-time-compare-price') || '';
      } else if (this.purchaseType.hasSubscription) {
        price = this.selectedButton.getAttribute('data-subscription-price') || '';
        comparePrice = this.selectedButton.getAttribute('data-subscription-compare-price') || '';
      }
      
      // Update price element
      if (price) {
        atcPriceElement.textContent = price;
      }
      
      // Update compare price element — clear any inline display style so CSS classes control visibility
      if (atcComparePriceElement) {
        if (comparePrice && comparePrice !== price) {
          atcComparePriceElement.textContent = comparePrice;
          atcComparePriceElement.classList.add('product__price--compare-active');
          atcComparePriceElement.style.removeProperty('display');
        } else {
          atcComparePriceElement.classList.remove('product__price--compare-active');
          atcComparePriceElement.style.removeProperty('display');
        }
      }
    }

    /**
     * Updates the shipping callout text and icon based on whether the
     * selected quantity option qualifies for free shipping.
     */
    updateShippingCallout() {
      const callout = document.querySelector('.td-shipping-callout');
      if (!callout || !this.selectedButton) return;

      if (callout.getAttribute('data-dynamic-callout') !== 'true') return;

      const threshold = Number(this.getAttribute('data-free-shipping-threshold')) || 0;
      let selectedPriceCents = 0;

      if (this.purchaseType.hasSubscription) {
        selectedPriceCents = Number(this.selectedButton.getAttribute('data-subscription-price-cents')) || 0;
      } else {
        selectedPriceCents = Number(this.selectedButton.getAttribute('data-one-time-price-cents')) || 0;
      }

      const qualifies = selectedPriceCents >= threshold;
      const textEl = callout.querySelector('.td-shipping-callout__text');

      if (qualifies) {
        callout.classList.add('td-shipping-callout--qualified');
        if (textEl) {
          textEl.textContent = callout.getAttribute('data-qualified-text') || textEl.textContent;
        }
      } else {
        callout.classList.remove('td-shipping-callout--qualified');
        if (textEl) {
          textEl.textContent = callout.getAttribute('data-default-text') || textEl.textContent;
        }
      }
    }

    /**
     * Public method to get current purchase type state
     * @returns {Object} Current purchase type state
     */
    getPurchaseType() {
      return { ...this.purchaseType };
    }
  }

  customElements.define('td-bundle-quantity-selector', TdBundleQuantitySelector);
}
